import {MenuItemPayload} from './types';
import * as XLSX from 'xlsx';

function parseRows(csv: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = '';
  let quoted = false;

  for (let index = 0; index < csv.length; index += 1) {
    const character = csv[index];
    if (quoted && character === '"' && csv[index + 1] === '"') {
      field += '"';
      index += 1;
    } else if (character === '"') {
      quoted = !quoted;
    } else if (character === ',' && !quoted) {
      row.push(field.trim()); field = '';
    } else if ((character === '\n' || character === '\r') && !quoted) {
      if (character === '\r' && csv[index + 1] === '\n') index += 1;
      row.push(field.trim());
      if (row.some(value => value !== '')) rows.push(row);
      row = []; field = '';
    } else {
      field += character;
    }
  }
  row.push(field.trim());
  if (row.some(value => value !== '')) rows.push(row);
  if (quoted) throw new Error('The CSV contains an unclosed quoted value.');
  return rows;
}

export function parseMenuCsv(csv: string): MenuItemPayload[] {
  const rows = parseRows(csv.replace(/^\uFEFF/, ''));
  if (rows.length < 2) throw new Error('The CSV must contain a header and at least one menu item.');

  const normalizedRows = rows.map(row => row.map(value => value.toLowerCase().replace(/[^a-z0-9]/g, '')));
  const standardHeaderIndex = normalizedRows.findIndex(headers => ['name', 'category', 'price'].every(header => headers.includes(header)));
  const drinkHeaderIndex = normalizedRows.findIndex(headers => headers.some(header =>
    ['skuname', 'sellingpriceperglass', 'sellingpricepertot', 'sellingpriceperbtl', 'sellingpriceperbottle', 'pricebyglass', 'pricebybottle'].includes(header),
  ));
  const headerIndex = standardHeaderIndex >= 0 ? standardHeaderIndex : drinkHeaderIndex;
  if (headerIndex < 0) throw new Error('Could not find menu headers. Use the template or a drinks CSV with selling-price columns.');
  const headers = normalizedRows[headerIndex];

  if (standardHeaderIndex < 0) {
    const find = (...names: string[]) => headers.findIndex(header => names.includes(header));
    const skuIndex = find('skuname');
    const glassIndex = find('sellingpriceperglass', 'sellingpricepertot', 'pricebyglass');
    const bottleIndex = find('sellingpriceperbtl', 'sellingpriceperbottle', 'pricebybottle');
    const mlIndex = find('ml');
    const uomIndex = find('uom', 'unitofmeasurement', 'unitofmeasure');
    const isSpirits = skuIndex >= 0;
    const isOtherDrinks = headers.includes('pricebyglass') || headers.includes('pricebybottle');
    const nameIndex = isSpirits ? skuIndex : 1;
    const groupIndex = isOtherDrinks ? 0 : -1;
    const originIndex = !isSpirits && !isOtherDrinks ? 0 : -1;
    let currentSection = '';
    const imported: MenuItemPayload[] = [];

    rows.slice(headerIndex + 1).forEach((row, offset) => {
      const clean = (index: number) => index >= 0 ? (row[index] || '').trim() : '';
      const parsePrice = (index: number) => {
        const raw = clean(index).replace(/[, ]/g, '');
        const number = Number(raw);
        return raw && raw.toLowerCase() !== 'x' && Number.isFinite(number) && number > 0 ? number : undefined;
      };
      const name = clean(nameIndex);
      const priceGlass = parsePrice(glassIndex);
      const priceBottle = parsePrice(bottleIndex);
      const mlText = clean(mlIndex);
      const uom = clean(uomIndex);

      if (!priceGlass && !priceBottle) {
        const heading = clean(groupIndex) || name || clean(originIndex);
        if (heading && !mlText) currentSection = heading;
        return;
      }
      if (!name) return;
      if (clean(groupIndex)) currentSection = clean(groupIndex);
      const servingSizeMl = mlText && Number.isFinite(Number(mlText)) ? Number(mlText) : undefined;
      imported.push({
        name,
        category: 'drink',
        price: priceGlass || priceBottle || 0,
        priceGlass,
        priceBottle,
        menuSection: currentSection || (isSpirits ? 'Spirits' : 'Wine'),
        origin: clean(originIndex) || undefined,
        servingSizeMl,
        description: uom || [clean(originIndex), servingSizeMl ? `${servingSizeMl}ml` : ''].filter(Boolean).join(' · '),
        imageUrl: '',
        isAvailable: true,
        sortOrder: imported.length,
      });
    });
    if (!imported.length) throw new Error('No drink rows with a glass, tot, or bottle selling price were found.');
    if (imported.length > 500) throw new Error('The CSV contains more than the maximum 500 priced drink items.');
    return imported;
  }

  const required = ['name', 'category', 'price'];
  const missing = required.filter(header => !headers.includes(header));
  if (missing.length) throw new Error(`Missing required column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}.`);
  const value = (row: string[], key: string) => row[headers.indexOf(key)] || '';

  const namedRows = rows.slice(headerIndex + 1).filter(row => value(row, 'name').trim() !== '');
  if (!namedRows.length) throw new Error('No named menu items were found in the file.');
  return namedRows.map((row, index) => {
    const line = index + headerIndex + 2;
    const name = value(row, 'name').trim();
    const category = value(row, 'category').toLowerCase();
    const price = Number(value(row, 'price'));
    const sortOrderText = value(row, 'sortorder');
    const visibility = value(row, 'isavailable').toLowerCase();
    if (name.length < 2) throw new Error(`Row ${line}: name must contain at least 2 characters.`);
    if (category !== 'food' && category !== 'drink') throw new Error(`Row ${line}: category must be food or drink.`);
    if (!Number.isFinite(price) || price < 0) throw new Error(`Row ${line}: price must be zero or greater.`);
    if (visibility && !['true', 'false', 'yes', 'no', '1', '0'].includes(visibility)) throw new Error(`Row ${line}: isAvailable must be true or false.`);
    const sortOrder = sortOrderText ? Number(sortOrderText) : index;
    if (!Number.isInteger(sortOrder) || sortOrder < 0) throw new Error(`Row ${line}: sortOrder must be a whole number of zero or greater.`);
    return {
      name,
      category,
      price,
      description: value(row, 'description'),
      imageUrl: value(row, 'imageurl'),
      isAvailable: visibility ? ['true', 'yes', '1'].includes(visibility) : true,
      sortOrder,
    } as MenuItemPayload;
  });
}

export const DRINK_CSV_TEMPLATE = 'Sku Name,ML,UOM,Selling price per tot,Selling price per btl\nHouse Gin,750,30ml tot,10000,150000\nSparkling Water,500,500ml bottle,,15000\n';
export const FOOD_CSV_TEMPLATE = 'name,foodCategory,price,description,imageUrl,isAvailable,sortOrder\nBruschetta,Starter,18000,"Tomato, basil and toasted bread",,true,1\nGrilled Fish,Main,32000,Fresh catch with seasonal vegetables,,true,2\nChocolate Cake,Dessert,16000,Dark chocolate cake,,true,3\nTruffle Fries,Sides,12000,Crispy fries with truffle seasoning,,true,4\n';

const normalizeHeader = (value: string) => value.toLowerCase().replace(/[^a-z0-9]/g, '');

export function parseFoodMenuCsv(csv: string, defaultCategory = 'Food'): MenuItemPayload[] {
  const rows = parseRows(csv.replace(/^\uFEFF/, ''));
  const normalizedRows = rows.map(row => row.map(normalizeHeader));
  const nameHeaders = ['name', 'itemname', 'foodname', 'dishname', 'menuname'];
  const priceHeaders = ['price', 'sellingprice', 'menuprice', 'unitprice'];
  const headerIndex = normalizedRows.findIndex(row =>
    row.some(header => nameHeaders.includes(header)) && row.some(header => priceHeaders.includes(header)),
  );
  if (headerIndex < 0) throw new Error('Could not find food name and price columns. Download the food template to see the supported format.');
  const headers = normalizedRows[headerIndex];
  const find = (...names: string[]) => headers.findIndex(header => names.includes(header));
  const nameIndex = find(...nameHeaders);
  const priceIndex = find(...priceHeaders);
  const categoryIndex = find('category', 'foodcategory', 'course', 'section', 'menusection');
  const descriptionIndex = find('description', 'itemdescription', 'details', 'uom');
  const imageIndex = find('imageurl', 'image', 'photo', 'photourl');
  const availableIndex = find('isavailable', 'available', 'publish', 'visible');
  const orderIndex = find('sortorder', 'displayorder', 'order');
  const items: MenuItemPayload[] = [];

  rows.slice(headerIndex + 1).forEach((row, offset) => {
    const get = (index: number) => index >= 0 ? (row[index] || '').trim() : '';
    const name = get(nameIndex);
    if (!name) return;
    const priceText = get(priceIndex).replace(/[^0-9.-]/g, '');
    const price = Number(priceText);
    if (!priceText || !Number.isFinite(price) || price < 0) throw new Error(`Row ${headerIndex + offset + 2}: price must be zero or greater.`);
    const visibility = get(availableIndex).toLowerCase();
    if (visibility && !['true', 'false', 'yes', 'no', '1', '0'].includes(visibility)) throw new Error(`Row ${headerIndex + offset + 2}: availability must be true or false.`);
    const orderText = get(orderIndex);
    const sortOrder = orderText ? Number(orderText) : items.length;
    if (!Number.isInteger(sortOrder) || sortOrder < 0) throw new Error(`Row ${headerIndex + offset + 2}: display order must be a whole number of zero or greater.`);
    items.push({
      name,
      category: 'food',
      menuSection: get(categoryIndex) || defaultCategory,
      price,
      description: get(descriptionIndex),
      imageUrl: get(imageIndex),
      isAvailable: visibility ? ['true', 'yes', '1'].includes(visibility) : true,
      sortOrder,
    });
  });
  if (!items.length) throw new Error('No named food items were found in the file.');
  return items;
}

export function parseFoodMenuWorkbook(data: ArrayBuffer): MenuItemPayload[] {
  const workbook = XLSX.read(data, {type: 'array'});
  const items: MenuItemPayload[] = [];
  const errors: string[] = [];
  workbook.SheetNames.forEach(sheetName => {
    const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName], {blankrows: false});
    if (!csv.trim()) return;
    try { items.push(...parseFoodMenuCsv(csv, sheetName)); }
    catch (error) { errors.push(`${sheetName}: ${error instanceof Error ? error.message : 'unsupported layout'}`); }
  });
  if (!items.length) throw new Error(errors.length ? `No importable food worksheets found. ${errors.join(' ')}` : 'The workbook is empty.');
  if (items.length > 500) throw new Error(`The workbook contains ${items.length} food items; the maximum is 500.`);
  return items.map((item, index) => ({...item, sortOrder: index}));
}

export function parseMenuWorkbook(data: ArrayBuffer): MenuItemPayload[] {
  const workbook = XLSX.read(data, {type: 'array'});
  const items: MenuItemPayload[] = [];
  const errors: string[] = [];

  workbook.SheetNames.forEach(sheetName => {
    const csv = XLSX.utils.sheet_to_csv(workbook.Sheets[sheetName], {blankrows: false});
    if (!csv.trim()) return;
    try {
      items.push(...parseMenuCsv(csv));
    } catch (error) {
      errors.push(`${sheetName}: ${error instanceof Error ? error.message : 'unsupported layout'}`);
    }
  });

  if (!items.length) {
    throw new Error(errors.length ? `No importable worksheets found. ${errors.join(' ')}` : 'The workbook is empty.');
  }
  if (items.length > 500) throw new Error(`The workbook contains ${items.length} priced items; the maximum is 500.`);
  return items.map((item, index) => ({...item, sortOrder: index}));
}
